
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, collection, getDocs, writeBatch, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import Layout from '../../components/Layout';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import IconButton from '../../ui/IconButton';
import Tabs from '../../ui/Tabs/Tabs';
import { Field, Input, FormFooter } from '../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { DEFAULT_ROLES, PERMISSIONS, MODULE_LABELS, MODULE_ORDER, PERMISSION_GROUPS } from '../../constants/permissions';
import { CATALOG_VERSION, upgradeRole } from '../../constants/permissionCatalog';
import { levelOf, LEVEL_WORDS, LEVEL_TONE } from '../../constants/permissionLevels';
import PermissionsEditor, { labelOf } from '../../components/PermissionsEditor';
import { rolePushFor } from '../../utils/permissionOverrides';
import { viewAsRole, rolesDraft, rolesSignature } from '../../utils/viewAsRole';
import { holds, usePermissions } from '../../hooks/usePermissions';
import { firstPagePath } from '../../ui/AppShell/navigation';
import logger from '../../utils/logger';
import { useModal } from '../../contexts/ModalContext';
import { PROTECTED_ROLES, newRoleIdFor, rolesFromDoc, usersOfRole, describeUsers, roleChanges, capHistory } from './roleRules';
import { useAuth } from '../../contexts/AuthContext';
import { dubaiDateTime } from '../../utils/dubaiTime';
import { adminService } from '../../services/adminService';

// the change log kept with the roles (owner's permissions plan, 3 Oct 2026: "كل حفظ في صفحة الأدوار يتسجل: مين، إمتى،
// وإيه الصلاحيات اللي اتزادت أو اتشالت من أنهي دور") - kept to its newest entries that fit the document (capHistory)
const PAGES = MODULE_ORDER.filter((g) => PERMISSION_GROUPS[g]);
const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso || '') : dubaiDateTime(d);
};
const listOf = (keys, max = 6) => {
  const words = (keys || []).map(labelOf);
  return words.length > max ? `${words.slice(0, max).join(', ')} and ${words.length - max} more` : words.join(', ');
};

const RoleManagement = () => {
  // Hooks first: fetchRoles lists showToast as a dependency, so the modal hook must run
  // before it (the old order threw "Cannot access before initialization" and the page
  // never rendered).
  const { showConfirm, showToast } = useModal();
  const navigate = useNavigate();
  // Saving writes settings/roles and every user profile: the database rules allow both to the Admin
  // role only, so the page offers the writes to it only (ADM-11) - manage_roles alone may look.
  const { currentUser } = useAuth();
  // the roles are the Admin role's own power (roles_edit, ADMIN_LOCKED - owner, 4 Oct 2026); through usePermissions so
  // "show the app as a role" shows a role's view
  const { hasPermission: may } = usePermissions();
  const canWrite = may('roles_edit');

  // 'loading' | 'ready' | 'error'. Save / Add / Delete only run on 'ready': the page starts
  // from the code's DEFAULT_ROLES, and saving those after a failed (or unfinished) read
  // overwrote every custom role and pushed the defaults to all users (audit ADM-10).
  const [rolesStatus, setRolesStatus] = useState('loading');
  const [rolesError, setRolesError] = useState('');
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  // the roles as the editor got them (read, or just saved): an edit since is what a draft keeps
  const [baseline, setBaseline] = useState(DEFAULT_ROLES);
  // what the database holds: the roles as saved (the change log's "before"), their catalogue version, the log, and
  // whether the roles document exists at all
  const [stored, setStored] = useState({ roles: DEFAULT_ROLES, version: 1, history: [], exists: false });
  const [selectedRole, setSelectedRole] = useState('admin');
  const [saving, setSaving] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [showAddRole, setShowAddRole] = useState(false);
  const [tab, setTab] = useState('edit');

  // Logic to fetch roles from Firestore. Only the latest read applies (React's StrictMode runs the mount twice in
  // development: the earlier read finished last and replaced the roles - and the edits given back - final review 3
  // Oct 2026)
  const fetchSeq = useRef(0);
  const fetchRoles = useCallback(async () => {
    const mine = ++fetchSeq.current;
    setRolesStatus('loading');
    setRolesError('');
    try {
      const rolesRef = doc(db, 'settings', 'roles');
      const rolesDoc = await getDoc(rolesRef);
      if (mine !== fetchSeq.current) return;                       // a newer read is on its way
      const exists = rolesDoc.exists();
      const data = exists ? rolesDoc.data() : null;
      let fresh = DEFAULT_ROLES;                                     // what the editor starts from
      if (exists && data?.roles) {
        // Admin always holds every permission (the app treats the role as all-powerful
        // anyway); newly added permissions are folded in so the list never looks short.
        const loaded = rolesFromDoc({ exists, data }, DEFAULT_ROLES);
        // keys removed from the catalogue (24 Sep 2026 audit) drop off stored roles here - the upgrade below names the
        // ones that left on 3 Oct 2026 in the change log
        const known = new Set(Object.values(PERMISSIONS));
        const version = Number(data.permissionsVersion) || 1;
        const kept = { ...loaded };
        for (const id of Object.keys(kept)) {
          if (!kept[id]?.permissions) continue;
          // roles not upgraded yet are shown (and edited) upgraded: what the admin sees is what Save writes - an
          // untick stays unticked, a role added now gets only what is ticked (review 3 Oct 2026)
          const list = version < CATALOG_VERSION ? upgradeRole(kept[id].permissions).permissions : kept[id].permissions;
          kept[id] = { ...kept[id], permissions: list.filter((x) => known.has(x)) };
        }
        if (kept.admin) kept.admin = { ...kept.admin, permissions: Object.values(PERMISSIONS) };
        fresh = kept;
        setStored({ roles: loaded, version, history: Array.isArray(data.history) ? data.history : [], exists: true });
      } else {
        setStored({ roles: {}, version: 1, history: [], exists: false });
      }
      setRoles(fresh);
      setBaseline(fresh);
      const draft = rolesDraft.take();
      if (draft && draft.basedOn !== rolesSignature(fresh)) {
        // saved meanwhile (another tab, another admin): never restored over what was saved
        showToast('The roles were saved meanwhile, so your earlier unsaved changes were dropped.', 'warning');
      } else if (draft) {
        // the admin role always holds everything, whatever the draft says
        setRoles({ ...draft.roles, admin: { ...(draft.roles.admin || { name: 'Admin' }), permissions: Object.values(PERMISSIONS) } });
        if (draft.selectedRole && draft.roles[draft.selectedRole]) setSelectedRole(draft.selectedRole);
        showToast('Your unsaved changes are back - press Save Changes to keep them.', 'info');
      }
      setRolesStatus('ready');
    } catch (error) {
      if (mine !== fetchSeq.current) return;
      logger.error("❌ Error fetching roles:", error);
      setRolesStatus('error');
      setRolesError(error?.code || error?.message || 'unknown error');
      showToast("Failed to load roles from database.", 'error');
    }
  }, [showToast]);

  useEffect(() => {
    fetchRoles();
    // leaving the page: a read still on its way applies nothing (and leaves a kept draft for the next visit)
    const seq = fetchSeq;
    return () => { seq.current += 1; };
  }, [fetchRoles]);

  const rolesReady = rolesStatus === 'ready';

  // the roles not upgraded to the catalogue yet: what each would gain - what it can do today (owner: "كل دور ياخد
  // الصلاحيات الجديدة اللي بتساوي اللي بيعمله النهارده")
  const upgrade = useMemo(() => {
    // nothing stored yet: the built-in roles are already the page-by-page ones - no notice of "+0 new"
    if (!rolesReady || !stored.exists || stored.version >= CATALOG_VERSION) return null;
    return Object.entries(roles).filter(([id]) => id !== 'admin')
      .map(([id, r]) => ({ id, name: r.name, ...upgradeRole((stored.roles[id] || r).permissions) }));
  }, [rolesReady, stored, roles]);

  const handleAddRole = () => {
    if (!newRoleName.trim() || !rolesReady || !canWrite) return;

    // A name that matches an existing role used to replace it with an empty one (ADM-09)
    const { id: roleId, error } = newRoleIdFor(newRoleName, roles);
    if (error) {
      showToast(error, 'error');
      return;
    }
    setRoles(prev => ({
      ...prev,
      [roleId]: {
        name: newRoleName.trim(),
        permissions: []
      }
    }));
    setNewRoleName('');
    setShowAddRole(false);
    setSelectedRole(roleId);
  };

  const handleDeleteRole = async (roleId) => {
    if (!canWrite) return;
    if (PROTECTED_ROLES.includes(roleId)) {
      showToast('Cannot delete system roles', 'error');
      return;
    }
    if (!rolesReady) {
      showToast('The roles are not loaded yet - reload them before deleting one.', 'error');
      return;
    }
    if (!stored.exists) {
      // nothing saved yet: deleting would write an empty list of roles (review 3 Oct 2026)
      showToast('The roles were never saved - press Save Changes once, then delete the role.', 'error');
      return;
    }

    // A role that still has users is never deleted (ADM-08): they would keep the old list for ever.
    let holders;
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      holders = usersOfRole(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })), roleId);
    } catch (error) {
      logger.error('Error reading the users of a role:', error);
      showToast('Could not check who holds this role, so it was not deleted: ' + (error?.code || error?.message || 'unknown error'), 'error');
      return;
    }
    if (holders.length > 0) {
      showToast(`"${roles[roleId]?.name || roleId}" still has ${holders.length} user(s) (${describeUsers(holders)}). Give them another role in User Management first.`, 'error');
      return;
    }

    showConfirm({
      title: `Delete Role "${roles[roleId].name}"?`,
      message: "This action cannot be undone and will be saved to the database immediately.",
      isDangerous: true,
      confirmLabel: "Delete & Save",
      onConfirm: async () => {
        try {
          const newRoles = { ...roles };
          delete newRoles[roleId];
          // the stored roles minus this one (unsaved edits of the others stay unsaved); the version and the log kept
          const savedRoles = { ...stored.roles };
          delete savedRoles[roleId];
          const entry = { at: new Date().toISOString(), by: whoSaves, role: roleId, name: roles[roleId]?.name || roleId,
            added: [], removed: [...(stored.roles[roleId]?.permissions || [])], note: 'role deleted' };
          const history = capHistory([...stored.history, entry]);
          await setDoc(doc(db, 'settings', 'roles'), {
            roles: savedRoles,
            last_updated: new Date(),
            permissionsVersion: stored.version,
            history,
          });

          setRoles(newRoles);
          setBaseline((b) => { const n = { ...b }; delete n[roleId]; return n; });
          setStored((s) => ({ ...s, roles: savedRoles, history }));
          setSelectedRole('admin');
          showToast("Role deleted and saved successfully", 'success');
        } catch (error) {
          logger.error("Error deleting role:", error);
          showToast("Failed to delete role from database", 'error');
        }
      }
    });
  };

  // the change log's "by": the admin's name (the roles document is read by every user - no e-mail in it)
  const whoSaves = currentUser?.name || currentUser?.displayName || 'Admin';

  const setRolePermissions = (roleId, permissions) => {
    setRoles(prev => ({ ...prev, [roleId]: { ...prev[roleId], permissions } }));
  };

  // Save: the roles, their catalogue version and the change log; every user of a role gets exactly its list. Roles not
  // yet upgraded were upgraded when they were read (fetchRoles), so a save never ends the fallback (usePermissions)
  // without giving the new permissions - no one is locked out - and never adds what the admin unticked.
  const handleSaveRoles = async () => {
    if (!rolesReady) {
      showToast('The roles could not be loaded, so nothing was saved (saving now would replace every role with the built-in defaults).', 'error');
      return;
    }
    if (!canWrite) return;
    setSaving(true);
    try {
      const toSave = { ...roles };
      const at = new Date().toISOString();
      const entries = roleChanges(stored.roles, toSave).map((c) => ({ at, by: whoSaves, ...c,
        ...(stored.version < CATALOG_VERSION && !c.note ? { note: 'upgraded to the page-by-page permissions' } : {}) }));
      const history = capHistory([...stored.history, ...entries]);
      const rolesRef = doc(db, 'settings', 'roles');
      // FIXED: Added last_updated to satisfy security rules
      await setDoc(rolesRef, {
        roles: toSave,
        last_updated: new Date(),
        permissionsVersion: CATALOG_VERSION,
        history,
      });
      setRoles(toSave);
      setBaseline(toSave);
      setStored({ roles: toSave, version: CATALOG_VERSION, history, exists: true });
      // the activity log (Administration > Activity log): roles are written to Firebase, not through the engine
      adminService.logUserAction('SAVE_ROLES', `Saved roles: ${entries.map((c) => c.name).join(', ') || 'no permission changed'}`,
        currentUser, { source: 'web', changes: entries.map(({ role, added, removed, note }) => ({ role, added, removed, ...(note ? { note } : {}) })) });

      let updateCount = 0;
      const orphans = [];
      try {
        const batch = writeBatch(db);
        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);

        snapshot.docs.forEach(userDoc => {
          const userData = userDoc.data();
          // The role is the only source (ADM-07): every user of a role gets exactly its list and
          // any old personal exception is cleared - it used to be re-applied on every save.
          const push = rolePushFor(toSave, userData);
          if (push) {
            batch.set(doc(db, 'users', userDoc.id), { ...push, permissionsVersion: CATALOG_VERSION }, { merge: true });
            updateCount++;
          } else if (userData.role && userData.role !== 'pending') {
            orphans.push({ id: userDoc.id, ...userData });
          }
        });

        if (updateCount > 0) {
          await batch.commit();
        }
      } catch (e) {
        logger.error("Failed to propagate to users:", e);
        showToast('Roles saved globally, but failed to update existing users.', 'warning');
        return;
      }

      if (orphans.length > 0) {
        // users left from a role deleted before ADM-08: nothing to push them, so say who they are
        showToast(`Roles saved. ${orphans.length} user(s) hold a role that no longer exists (${describeUsers(orphans)}) - give them a role in User Management.`, 'warning');
        return;
      }
      showToast('Roles and permissions saved successfully!', 'success');
    } catch (error) {
      logger.error("Error saving roles:", error);
      showToast('Failed to save roles: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // "Show the app as this role" - its permissions as they stand here, saved or not; nothing is written. It opens the
  // first page the role can open (a role without the Overview landed on "Access Denied" - review 3 Oct 2026)
  const viewAs = (roleId) => {
    const r = roles[roleId];
    if (!r || roleId === 'admin' || !rolesReady) return;
    const permissions = r.permissions || [];
    if (rolesSignature(roles) !== rolesSignature(baseline)) rolesDraft.save({ roles, selectedRole, basedOn: baseline });
    viewAsRole.set({ roleId, name: r.name, permissions });
    const seen = { role: roleId, permissions, permissionsVersion: CATALOG_VERSION };
    navigate(firstPagePath((p) => holds(seen, p)));
  };

  // a cell of "Who can do what" opens that page of that role in the editor (scrolled to once the editor is drawn)
  const [scrollTo, setScrollTo] = useState(null);
  const openPage = (roleId, pageId) => {
    setSelectedRole(roleId);
    setTab('edit');
    setScrollTo(pageId);
  };
  useEffect(() => {
    if (!scrollTo || tab !== 'edit') return;
    document.getElementById(`perm-page-${scrollTo}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    setScrollTo(null);
  }, [scrollTo, tab]);

  const roleIds = Object.keys(roles);
  const history = [...stored.history].reverse();

  return (
    <Layout title="Role Management" subtitle="Each page, its tabs and its buttons - by role" backPath="/admin/users">
      {upgrade && canWrite && (
        <div className="mb-4 rounded-xl border border-purple-200 bg-purple-50 p-4 flex flex-col gap-2" role="status">
          <div className="font-semibold text-purple-900">The permissions are now page by page: opening the page, each tab, each button</div>
          <div className="text-sm text-purple-900">
            Each role gets the new permissions that match what it can do today, so nothing changes for anyone. The
            permissions no button uses are removed. Saving does it - or press the button.
          </div>
          <ul className="text-sm text-gray-800 list-disc ml-5">
            {upgrade.map((u) => (
              <li key={u.id}><b>{u.name}</b>: +{u.added.length} new{u.dropped.length ? `, ${u.dropped.length} unused removed` : ''}
                {u.added.length ? <span className="text-gray-600"> ({listOf(u.added, 5)})</span> : null}</li>
            ))}
          </ul>
          <div>
            <Button variant="primary" size="sm" onClick={handleSaveRoles} disabled={saving} isLoading={saving}>Upgrade and save</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <Card
            title="Roles"
            actions={canWrite ? <IconButton icon={ICONS.plus} label="Add role" size="sm" variant="secondary" onClick={() => setShowAddRole(true)} disabled={!rolesReady} /> : null}
          >
            {rolesStatus === 'loading' && (
              <p className="text-sm text-gray-600 mb-3" role="status">Loading the roles...</p>
            )}
            {rolesStatus === 'error' && (
              <div className="mb-3 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700" role="alert">
                The roles could not be loaded ({rolesError}). The list below is the built-in default, not your roles, so saving is blocked.
                <div className="mt-2">
                  <Button size="sm" variant="secondary" onClick={fetchRoles} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Retry</Button>
                </div>
              </div>
            )}
            {showAddRole && (
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <Field label="Role name">
                  <Input type="text" size="sm" placeholder="Role name" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddRole()} autoFocus />
                </Field>
                <div className="flex justify-end gap-2 mt-2">
                  <Button onClick={() => { setShowAddRole(false); setNewRoleName(''); }} size="sm" variant="secondary">Cancel</Button>
                  <Button onClick={handleAddRole} size="sm" variant="primary">Add</Button>
                </div>
              </div>
            )}

            <div className="space-y-1">
              {Object.entries(roles).map(([roleId, role]) => (
                <div key={roleId} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setSelectedRole(roleId)}
                    aria-pressed={selectedRole === roleId}
                    className={`flex-1 text-left px-3 py-2 rounded-lg border transition-colors ${selectedRole === roleId ? 'bg-purple-50 border-purple-300 text-purple-800' : 'bg-white border-transparent hover:bg-gray-50'}`}
                  >
                    <div className="text-sm font-medium">{role.name}</div>
                    <div className="text-xs text-gray-500">{role.permissions?.length || 0} permissions</div>
                  </button>
                  {/* only once the real roles are read - before that the list is the built-in default */}
                  {canWrite && rolesReady && roleId !== 'admin' && (
                    <IconButton icon={ICONS.eye} label={`Show the app as ${role.name}`} size="sm" onClick={() => viewAs(roleId)} />
                  )}
                  {!PROTECTED_ROLES.includes(roleId) && rolesReady && canWrite && (
                    <IconButton icon={ICONS.trash} label={`Delete role ${role.name}`} size="sm" tone="danger" onClick={() => handleDeleteRole(roleId)} />
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="lg:col-span-3 flex flex-col gap-4">
          <Tabs value={tab} onChange={setTab} idPrefix="roles-tab" label="Role management sections"
            items={[{ id: 'edit', label: 'Permissions' }, { id: 'matrix', label: 'Who can do what' },
              { id: 'log', label: 'Change log', count: stored.history.length || undefined }]} />

          {tab === 'edit' && (
            <Card title={`Permissions for ${roles[selectedRole]?.name || ''}`} id="roles-tab-panel-edit" role="tabpanel">
              <p className="text-sm text-gray-700 mb-4">
                Each page in three levels: opening it, its tabs, its buttons by kind. The shortcuts set a whole page at once.
                The role is the only source of its users' permissions: saving gives every user of the role exactly this
                list. A role that still has users cannot be deleted.
              </p>
              {!canWrite && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4" role="status">
                  Only the Admin role can change roles - you can look, not save.
                </p>
              )}
              <PermissionsEditor
                value={roles[selectedRole]?.permissions || []}
                onChange={(next) => setRolePermissions(selectedRole, next)}
                readOnly={!canWrite || selectedRole === 'admin'}
              />
              {canWrite && (
              <FormFooter sticky className="mt-6">
                <Button variant="primary" onClick={handleSaveRoles} disabled={saving || !rolesReady} isLoading={saving} leadingIcon={<Icon glyph={ICONS.save} size="sm" />}
                  title={rolesReady ? undefined : 'The roles are not loaded, so they cannot be saved'}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </FormFooter>
              )}
            </Card>
          )}

          {tab === 'matrix' && (
            <Card title="Who can do what" id="roles-tab-panel-matrix" role="tabpanel">
              <p className="text-sm text-gray-700 mb-3">Every role on every page. A cell opens that page of that role.</p>
              <TableContainer maxHeight="70vh">
                <Table sticky>
                  <TableHead><TableRow>
                    <TableHeader>Page</TableHeader>
                    {roleIds.map((id) => <TableHeader key={id}>{roles[id].name}</TableHeader>)}
                  </TableRow></TableHead>
                  <TableBody>
                    {PAGES.map((pg) => (
                      <TableRow key={pg}>
                        <TableCell className="font-medium">{MODULE_LABELS[pg] || pg}</TableCell>
                        {roleIds.map((id) => {
                          const lv = id === 'admin' ? 'full' : levelOf(roles[id].permissions, pg);
                          return (
                            <TableCell key={id}>
                              <button type="button" onClick={() => openPage(id, pg)} aria-label={`${roles[id].name} on ${MODULE_LABELS[pg] || pg}: ${LEVEL_WORDS[lv]}`}
                                className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500">
                                <Badge size="sm" variant={LEVEL_TONE[lv]}>{LEVEL_WORDS[lv]}</Badge>
                              </button>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}

          {tab === 'log' && (
            <Card title="Change log" id="roles-tab-panel-log" role="tabpanel">
              {!history.length ? <p className="text-sm text-gray-600">No change saved yet.</p> : (
                <TableContainer maxHeight="70vh">
                  <Table sticky>
                    <TableHead><TableRow>
                      <TableHeader>When</TableHeader><TableHeader>By</TableHeader><TableHeader>Role</TableHeader>
                      <TableHeader>Added</TableHeader><TableHeader>Removed</TableHeader><TableHeader>Note</TableHeader>
                    </TableRow></TableHead>
                    <TableBody>
                      {history.map((h) => (
                        <TableRow key={`${h.at}-${h.role}-${h.note || ''}`}>
                          <TableCell className="font-mono">{when(h.at)}</TableCell>
                          <TableCell>{h.by || '—'}</TableCell>
                          <TableCell className="font-medium">{h.name || h.role}</TableCell>
                          <TableCell><div style={{ whiteSpace: 'normal', minWidth: 200, maxWidth: 360, margin: '0 auto' }} className="text-emerald-800">
                            {h.added?.length ? `+${h.added.length}: ${listOf(h.added)}` : '—'}</div></TableCell>
                          <TableCell><div style={{ whiteSpace: 'normal', minWidth: 200, maxWidth: 360, margin: '0 auto' }} className="text-red-700">
                            {h.removed?.length ? `−${h.removed.length}: ${listOf(h.removed)}` : '—'}</div></TableCell>
                          <TableCell>{h.note || ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RoleManagement;
